import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUser, loginWithWallet, logout, shortAddress } from "../lib/auth";
import { getNetworkLabel } from "../lib/runtimeConfig";
import {
  CHAIN_UNKNOWN,
  CHAIN_UNKNOWN_MESSAGE,
  explainWalletError,
  getSelectedRdns,
  isConnectionCancelled,
  isUserRejected,
  selectProvider,
  toWalletError,
} from "../lib/wallet";
import { errorReporter } from "../lib/errorReporter";
import { useWalletProviders, WalletPicker, walletOptions } from "./WalletPicker";

/**
 * With several ways in and no remembered choice, the first click opens a picker:
 * the pick is itself a click, so the wallet popup keeps its user gesture.
 */
export function WalletChip() {
  const navigate = useNavigate();
  const user = getUser();
  const providers = useWalletProviders();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [selectedRdns, setSelectedRdns] = useState<string | null>(() => getSelectedRdns());
  const [pickerOpen, setPickerOpen] = useState(false);

  const address = user?.kind === "wallet" ? user.address : undefined;
  const options = walletOptions(providers);
  const pinned = options.find((o) => o.rdns === selectedRdns) ?? null;
  const sole = options.length === 1 ? (options[0] ?? null) : null;
  const needsPick = options.length > 1 && pinned === null;

  function onFailure(err: unknown) {
    if (isConnectionCancelled(err)) {
      // The QR sheet was closed: nothing was rejected, so nothing to say.
      setNote(null);
    } else if (isUserRejected(err)) {
      // Declined in the wallet — an outcome, not an error.
      setNote("Rejected");
    } else if (toWalletError(err).code === CHAIN_UNKNOWN) {
      // Expected: the descriptor did not load at boot, and that was reported then.
      setNote(CHAIN_UNKNOWN_MESSAGE);
    } else {
      const walletError = toWalletError(err);
      errorReporter.captureException(walletError, {
        source: "wallet",
        code: walletError.code,
        stage: "connect",
      });
      setNote(explainWalletError(walletError));
    }
  }

  /**
   * Not async on purpose: `loginWithWallet` fires `eth_requestAccounts` before
   * its first await, and the wallet only opens its prompt while the click's
   * user gesture is still on the stack. Nothing above it may await.
   */
  function connectWith(rdns?: string) {
    if (busy) return;
    if (rdns !== undefined) {
      selectProvider(rdns); // synchronous localStorage write
      setSelectedRdns(rdns);
      setPickerOpen(false);
    }
    setBusy(true);
    setNote(null);
    loginWithWallet(undefined, rdns).then(
      () => {
        setBusy(false);
        navigate("/");
      },
      (err: unknown) => {
        setBusy(false);
        onFailure(err);
      },
    );
  }

  function handleChipClick() {
    if (needsPick && !pickerOpen) {
      setPickerOpen(true);
      return;
    }
    // Explicit: WalletConnect is outside the synchronous injected-provider chain.
    connectWith((pinned ?? sole)?.rdns);
  }

  const handleDisconnect = () => {
    logout();
    navigate("/login");
  };

  if (address !== undefined) {
    const network = getNetworkLabel();
    return (
      <div className="wallet-chip-wrap">
        <button
          type="button"
          className="wallet-chip wallet-chip-connected"
          onClick={handleDisconnect}
          title={`${address} — click to disconnect`}
        >
          <span className="wallet-dot" aria-hidden="true" />
          <span className="wallet-chip-address">{shortAddress(address)}</span>
          {network !== null && <span className="chain-badge">{network}</span>}
        </button>
      </div>
    );
  }

  return (
    <div className="wallet-chip-wrap">
      <button
        type="button"
        className="wallet-chip wallet-chip-connect"
        onClick={handleChipClick}
        disabled={busy}
      >
        {busy ? "Connecting…" : "Connect wallet"}
      </button>
      {pickerOpen && options.length > 1 && (
        <WalletPicker
          options={options}
          selectedRdns={selectedRdns}
          onPick={connectWith}
          disabled={busy}
        />
      )}
      {note !== null && (
        <span className="wallet-chip-note" title={note}>
          {note}
        </span>
      )}
    </div>
  );
}
